import { AnnouncementCard } from "client/Features/Workspace/Tiles/Hub/Card/AnnouncementCard";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import { ActiveFilter, HubViewChangeHandler } from "../common";
import { List } from "../Announcements/Overview/List/List";
import { AnnouncementPreviewItemModel } from "client/Api/Hub/Announcements/announcementViewer";
import { MediaCardGrid } from "./MediaCardGrid";
import styles from "./Overview.module.less";
import { useOverviewSection } from "./useOverviewSection";
import { ViewAll } from "./ViewAll";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubDataSource, HubItemDataSource } from "client/Api/Hub/hub";

export type AnnouncementSectionProps = {
	items: readonly AnnouncementPreviewItemModel[];
	isMobile: boolean;
	onViewChange: HubViewChangeHandler;
	onActiveFilterChange: (activeFilter: ActiveFilter | null) => void;
	onSelectCategory: (source: HubItemDataSource, categoryId: number | null) => void;
	timezone: TimeZone;
};

export const AnnouncementSection = (props: AnnouncementSectionProps): ReactElement => {
	const {
		items,
		isMobile,
		onViewChange,
		onActiveFilterChange,
		onSelectCategory,
		timezone
	} = props;

	const {
		viewAllHandler,
		viewItemHandler
	} = useOverviewSection(
		HubDataSource.Announcement,
		onViewChange,
		onActiveFilterChange,
		onSelectCategory
	);

	const renderItems = (): ReactElement => {
		if (isMobile) {
			return (
				<List
					items={items}
					trackingId="announcement-list-overview"
					onNavigateToItem={viewItemHandler}
					collapseMode={{
						kind: "limited-items",
						isCollapsed: true,
						maxItemsCount: MAX_MOBILE_ITEMS_TO_RENDER
					}}
					shouldAlwaysRenderMoreButton
					renderMoreButton={
						(
							<ViewAll
								itemSource={HubDataSource.Announcement}
								onClick={viewAllHandler}
								variant="list-item"
							/>
						)
					}
					timezone={timezone}
				/>
			);
		}

		return (
			<MediaCardList
				items={items}
				onNavigateToItem={viewItemHandler}
				timezone={timezone}
			/>
		);
	};

	return (
		<section>
			<header className={styles.sectionHeader}>
				<h3 className={styles.sectionTitle}>
					{localize("_LatestAnnouncements")}
				</h3>

				{!isMobile && (
					<ViewAll
						itemSource={HubDataSource.Announcement}
						onClick={viewAllHandler}
						variant="anchor"
					/>
				)}
			</header>

			{renderItems()}
		</section>
	);
};
AnnouncementSection.displayName = "AnnouncementSection";

const MAX_MOBILE_ITEMS_TO_RENDER = 3;

type MediaCardListProps = {
	items: readonly AnnouncementPreviewItemModel[];
	onNavigateToItem: (id: number) => void;
	timezone: TimeZone;
};

const MediaCardList = (props: MediaCardListProps): ReactElement => {
	const {
		items,
		onNavigateToItem,
		timezone
	} = props;

	return (
		<MediaCardGrid trackingId="announcement-grid">
			{items.map((x, i): ReactElement => (
				<AnnouncementCard
					key={x.id}
					item={x}
					onOpen={onNavigateToItem}
					size={i !== 0
						? "small"
						: "large"
					}
					timezone={timezone}
				/>
			))}
		</MediaCardGrid>
	);
};
MediaCardList.displayName = "MediaCardList";
